import { InfoOutlineIcon, WarningTwoIcon } from '@chakra-ui/icons';
import {
  Box,
  Input,
  Flex,
  Text,
  useToast,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import lodash from 'lodash';
import React, { useState, useEffect } from 'react';

import { validate as athenaValidate } from '@finley-technologies/athena-utilities';
import {
  finleyAssert,
  stringConversion as commonStringConversion,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  DataLake,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';

import { AthenaDataPreviewModal } from './AthenaDataPreviewModal';
import { AthenaRecurringDateField } from './AthenaRecurringDateField';
import { AthenaRecurringQueryExecuteModal } from './AthenaRecurringQueryExecuteModal';
import { BindingContainer } from './BindingContainer';
import { ColumnNamesContainer } from './ColumnNamesContainer';
import { inputWidth } from './consts';
import { QueryStringContainer } from './QueryStringContainer';
import { VariableContainer } from './VariableContainer';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  SearchIconButton,
  XIconButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const { cloneDeep } = lodash;

const createAthenaRecurringQueryShell = (
  idx: number,
): CreditAgreement.Config.AthenaConfig.RecurringQuery => ({
  name: `queryName_${idx}`,
  globalRecurringDateConfigFields: [],
  queryString: 'SQL query goes here!',
  variables: [],
  bindings: [],
  columnNames: [],
  defaultTo: { value: '', parseString: false },
});

const AthenaRecurringQueryView: React.FC<{
  recurringQuery: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  existingQueries: CreditAgreement.Config.AthenaConfig.RecurringQuery[];
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  basePropertyKey: string;
  idx: number;
  inputDateType: 'reportingDate' | 'triggerDate';
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  variableSubTitle?: string;
}> = ({
  recurringQuery,
  existingQueries,
  globalRecurringDateConfigs,
  wipConfig,
  fullPropertyKey,
  basePropertyKey,
  idx,
  inputDateType,
  presetVariables,
  validationVariables,
  variableSubTitle,
}) => {
  const toast = useToast();

  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[]
  >([]);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);
  const [isExecuteModalOpen, setIsExecuteModalOpen] = useState<boolean>(false);

  const addRecurringDateConfig = (wc: WipConfig): void => {
    const wipRecurringQuery = getIn(wc.formProps.values, fullPropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const recurringQueryCopy = cloneDeep(wipRecurringQuery);
    recurringQueryCopy.globalRecurringDateConfigFields.push('placeholder');
    wc.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);
  };

  const duplicateQuery = (): void => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const wipRecurringQueries = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQueries(
        wipRecurringQueries,
      ),
      `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
    );

    const copiedQuery = cloneDeep(recurringQuery);
    copiedQuery.name = `${recurringQuery.name}_copy`;

    const wipQueriesCopy = cloneDeep(wipRecurringQueries);
    wipQueriesCopy.splice(idx + 1, 0, copiedQuery);

    wipConfig.formProps.setFieldValue(basePropertyKey, wipQueriesCopy);
  };

  const deleteQuery = (wc: WipConfig): void => {
    const wipRecurringQueries = getIn(wc.formProps.values, basePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQueries(
        wipRecurringQueries,
      ),
      `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
    );

    wc.formProps.setFieldValue(
      basePropertyKey,
      wipRecurringQueries.filter((_, varIdx) => varIdx !== idx),
    );
  };

  const [isNameEditOpen, setNameEditOpen] = useState<boolean>(false);
  const [nameState, setNameState] = useState<string>(recurringQuery.name);
  const changeName = (): void => {
    if (!wipConfig) {
      return;
    }

    const nameExists = existingQueries.some((q) => q.name === nameState);
    if (nameExists) {
      toast({
        title: 'Updating SQL Name Error',
        description: `"${nameState}" name is already in use`,
        duration: 5000,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const recurringQueryCopy = cloneDeep(wipRecurringQuery);
    recurringQueryCopy.name = nameState;
    wipConfig.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);

    setNameEditOpen(false);
  };

  const [defaultValueState, setDefaultValueState] = useState<string>(
    recurringQuery.defaultTo.value,
  );
  const [defaultParseState, setDefaultParseState] = useState<boolean>(
    recurringQuery.defaultTo.parseString,
  );
  const [
    haveDefaultSettingsChanged,
    setHaveDefaultSettingsChanged,
  ] = useState<boolean>(false);

  useEffect(() => {
    if (!wipConfig) {
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const { value, parseString } = wipRecurringQuery.defaultTo;
    const valuesHaveChanged =
      value !== defaultValueState || parseString !== defaultParseState;
    setHaveDefaultSettingsChanged(valuesHaveChanged);
  }, [defaultParseState, defaultValueState, fullPropertyKey, wipConfig]);

  const updateDefaultSetting = (): void => {
    if (!wipConfig || !haveDefaultSettingsChanged) {
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const recurringQueryCopy = cloneDeep(wipRecurringQuery);
    recurringQueryCopy.defaultTo = {
      value: defaultValueState,
      parseString: defaultParseState,
    };
    wipConfig.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);
  };

  const changeQueryString = (newQueryString: string): void => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const recurringQueryCopy = cloneDeep(wipRecurringQuery);
    recurringQueryCopy.queryString = newQueryString;

    const errors = athenaValidate.validateAthenaRecurringQuery({
      athenaRecurringQuery: {
        ...recurringQueryCopy,
        variables: [
          ...validationVariables,
          ...presetVariables,
          ...recurringQueryCopy.variables,
        ],
      },
      globalRecurringDateConfigs,
    });

    if (errors.length) {
      toast({
        title: 'Updating SQL Query Error',
        description: errors.join(', '),
        duration: 5000,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
      return;
    }

    wipConfig.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);
  };

  return (
    <Box mb="2" p="3" border="1px" borderColor="gray.700">
      <Flex alignItems="center" mb={isQueryOpen ? '2' : undefined}>
        <Text fontWeight="bold" mr="2">
          Query: &quot;{recurringQuery.name}&quot;
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <SearchIconButton
              ariaLabel="execute query"
              label="Execute Query"
              onClick={() => setIsExecuteModalOpen(true)}
            />
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => duplicateQuery()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => deleteQuery(wipConfig)}
            />
            <IconButton
              aria-label={`${isQueryOpen ? 'close' : 'display'} config`}
              icon={isQueryOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setIsQueryOpen((prev) => !prev)}
              variant="ghost"
            />
          </>
        )}
      </Flex>

      {isQueryOpen && (
        <Box>
          {/* *** Query Name Management *** */}
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="5"
          >
            <Text width="40" mr="3">
              Query Name:
            </Text>
            {isNameEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <>
                <Input
                  mr="2"
                  type="text"
                  width={inputWidth}
                  value={nameState}
                  onChange={(e) => setNameState(e.target.value)}
                />
                <SaveIconButton
                  ariaLabel="save name"
                  label="Save Name"
                  onClick={() => changeName()}
                />
                <XIconButton
                  ariaLabel="close edit"
                  label="Close Edit"
                  onClick={() => {
                    setNameState(recurringQuery.name);
                    setNameEditOpen(false);
                  }}
                />
              </>
            ) : (
              <>
                <Text width={inputWidth} px="4">
                  {recurringQuery.name}
                </Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <EditIconButton
                    ariaLabel="edit name"
                    label="Edit Name"
                    onClick={() => setNameEditOpen(true)}
                    ml="2"
                  />
                )}
              </>
            )}
          </Flex>

          {/* *** Global Recurring Date Management *** */}
          <Box mb="5">
            <Flex alignItems="center">
              <Text fontWeight="bold" mr="2">
                Recurring Date Configs
              </Text>

              {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                <AddIconButton
                  ariaLabel="add recurring date field"
                  label="Add Recurring Date Field"
                  onClick={() => {
                    addRecurringDateConfig(wipConfig);
                  }}
                />
              )}
            </Flex>

            {wipConfig?.isMutateAllowed(basePropertyKey) && (
              <Text fontStyle="italic" fontSize="sm" mb="3">
                These &quot;fields&quot; should coincide with existing
                &quot;Global Recurring Date Config&quot; fields
              </Text>
            )}

            <Box border="1px" borderColor="gray.700" p="3">
              {recurringQuery.globalRecurringDateConfigFields.length > 0 ? (
                recurringQuery.globalRecurringDateConfigFields.map(
                  (field, index) => (
                    <AthenaRecurringDateField
                      key={`${fullPropertyKey}-recurring-date-${field}`}
                      wipConfig={wipConfig}
                      recurringDateField={field}
                      basePropertyKey={`${fullPropertyKey}.globalRecurringDateConfigFields`}
                      idx={index}
                    />
                  ),
                )
              ) : (
                <Text>No recurring dates configured</Text>
              )}
            </Box>
          </Box>

          {/* *** Variables Management *** */}
          <VariableContainer
            wipConfig={wipConfig}
            variables={recurringQuery.variables}
            subTitle={variableSubTitle}
            variablePropertyKey={`${fullPropertyKey}.variables`}
          />

          {/* *** Bindings Management *** */}
          <BindingContainer
            wipConfig={wipConfig}
            bindings={recurringQuery.bindings}
            bindingsPropertyKey={`${fullPropertyKey}.bindings`}
          />

          {/* *** SQL Query Management *** */}
          <QueryStringContainer
            wipConfig={wipConfig}
            fullPropertyKey={fullPropertyKey}
            queryString={recurringQuery.queryString}
            changeQueryString={changeQueryString}
            executeQueryMethod={() => setIsExecuteModalOpen(true)}
          />

          {/* *** Column Name Portion *** */}
          <ColumnNamesContainer
            wipConfig={wipConfig}
            columnNames={recurringQuery.columnNames}
            configPropertyKey={fullPropertyKey}
            executeQueryMethod={() => setIsExecuteModalOpen(true)}
          />

          {/* *** Default Setting Portion *** */}
          <Box mb="2">
            <Flex mb="2" alignItems="center">
              <Text mr="4" fontWeight="bold">
                Default Settings
              </Text>

              {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                <>
                  <Tooltip label="These default settings drive what value should be utilized instead if the referenced date configs do not output their respective max count">
                    <InfoOutlineIcon />
                  </Tooltip>

                  <SaveIconButton
                    ariaLabel="save default settings"
                    label="Save Default Settings"
                    onClick={() => {
                      updateDefaultSetting();
                    }}
                    disabled={!haveDefaultSettingsChanged}
                    mx="2"
                  />

                  {haveDefaultSettingsChanged && (
                    <Tooltip label="Updates are not yet saved!">
                      <WarningTwoIcon />
                    </Tooltip>
                  )}
                </>
              )}
            </Flex>

            <Flex mb="3" alignItems="center">
              <Text w="52">Value:</Text>

              {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
                <Input
                  type="text"
                  mr="2"
                  width="60"
                  value={defaultValueState}
                  onChange={(e) => {
                    setDefaultValueState(String(e.target.value ?? ''));
                  }}
                />
              ) : (
                <Text width={inputWidth} px="4">
                  {recurringQuery.defaultTo.value === ''
                    ? '[ empty string ]'
                    : recurringQuery.defaultTo.value}
                </Text>
              )}
            </Flex>

            <Flex mb="7" alignItems="center">
              <Text w="52">Parse String:</Text>

              <Box w="60">
                {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
                  <Select
                    value={commonUtils.toInitialCase(String(defaultParseState))}
                    onChange={(val) => {
                      if (val === undefined) {
                        return;
                      }
                      const booleanVal = commonStringConversion.convertStringBoolean(
                        val,
                      );
                      setDefaultParseState(booleanVal);
                    }}
                    options={['False', 'True'].map((value) => ({
                      label: value,
                      value,
                    }))}
                  />
                ) : (
                  <Text width={inputWidth} px="4">
                    {commonUtils.toInitialCase(
                      String(recurringQuery.defaultTo.parseString),
                    )}
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>
        </Box>
      )}

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <AthenaRecurringQueryExecuteModal
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          globalRecurringDateConfigs={globalRecurringDateConfigs}
          presetVariables={presetVariables}
          inputDateType={inputDateType}
          isOpen={isExecuteModalOpen}
          onCancel={() => setIsExecuteModalOpen(false)}
          onQuerySuccess={setQueryDataItems}
        />
      )}

      <AthenaDataPreviewModal
        queryDataItems={queryDataItems}
        onClose={() => setQueryDataItems([])}
      />
    </Box>
  );
};

export const AthenaRecurringQueriesContainer: React.FC<{
  wipConfig?: WipConfig;
  athenaRecurringQueries: CreditAgreement.Config.AthenaConfig.RecurringQuery[];
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  basePropertyKey: string;
  inputDateType: 'reportingDate' | 'triggerDate';
  presetVariables?: CreditAgreement.Config.QueryVariable[];
  validationVariables?: CreditAgreement.Config.QueryVariable[];
  variableSubTitle?: string;
}> = ({
  wipConfig,
  athenaRecurringQueries,
  globalRecurringDateConfigs,
  basePropertyKey,
  inputDateType,
  presetVariables = [],
  validationVariables = [],
  variableSubTitle,
}) => {
  const addQuery = (): void => {
    if (!wipConfig) {
      return;
    }

    const wipRecurringQueries = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQueries(
        wipRecurringQueries,
      ),
      `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
    );
    const recurringQueryCopy = cloneDeep(wipRecurringQueries);
    recurringQueryCopy.push(
      createAthenaRecurringQueryShell(recurringQueryCopy.length),
    );
    wipConfig.formProps.setFieldValue(basePropertyKey, recurringQueryCopy);
  };

  return (
    <Box mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Athena Recurring Queries
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add new recurring query"
            label="Add New Recurring Query"
            onClick={() => {
              addQuery();
            }}
          />
        )}
      </Flex>

      {athenaRecurringQueries.length > 0 ? (
        athenaRecurringQueries.map((query, idx) => (
          <AthenaRecurringQueryView
            key={`athena-recurring-query-view-${query.name}`}
            wipConfig={wipConfig}
            basePropertyKey={basePropertyKey}
            fullPropertyKey={`${basePropertyKey}[${idx}]`}
            recurringQuery={query}
            existingQueries={athenaRecurringQueries}
            globalRecurringDateConfigs={globalRecurringDateConfigs}
            idx={idx}
            inputDateType={inputDateType}
            presetVariables={presetVariables}
            validationVariables={validationVariables}
            variableSubTitle={variableSubTitle}
          />
        ))
      ) : (
        <Text p="4" border="1px" borderColor="gray.700">
          There are currently no Athena recurring queries
        </Text>
      )}
    </Box>
  );
};
